import { inject, injectable } from 'inversify'
import Debug from 'debug';
import { CommandQueue } from '@/app/shared/command-queue'
import { StateMachine, StateChangedListener } from '@/app/shared/state-machine'
import { ExamSessionService, ExamSessionServiceSymbol } from '@/app/exam-session/service';
import { ExamStoreModuleSymbol, ExamStoreModule } from '@/app/exam'
import { QuestionStoreModule, QuestionStoreModuleSymbol, LoadQuestionsJob } from '@/app/question/store';
import { ExamResultServiceSymbol, ExamResultService } from '@/app/exam/service';
import * as States from './states'
import * as Data from './data'

const debug = Debug('exam-state-machine')

type State = States.ExamInitialState | States.ExamStartingState | States.ExamInProgressState |
      States.ExamReviewState | States.ExamSummaryState | States.ExamErrorState |
      States.ExamCanceledState

@injectable()
export class ExamStateMachine {
  private data: Data.ExamStateMachineData = new Data.ExamStateMachineData()

  private stateMachine: StateMachine<State, States.ExamStateMachinePhase>

  public onTimeExpired?: () => void
  
  constructor(
    @inject(ExamStoreModuleSymbol)
    private examStore: ExamStoreModule,
    @inject(QuestionStoreModuleSymbol)
    private questionStore: QuestionStoreModule,
    @inject(ExamResultServiceSymbol)
    private examResultService: ExamResultService,
    @inject(ExamSessionServiceSymbol)
    private examSessionService: ExamSessionService) {

    this.stateMachine = new StateMachine<State, States.ExamStateMachinePhase>(
      'exam-state-machine')
    this.stateMachine.addStateChangedListener((phase) => {
      this.data.updatePhase(phase)
    })
  }

  initialize(state: Data.ExamStateMachineData) {
    this.data = state

    this.gotoInitialState()
  }

  start(options: States.ExamStartOptions) {
    this.stateMachine.executeAction('start', options)
  }

  navigate(questionIndex: number) {
    this.stateMachine.executeAction('navigate', questionIndex)
  }

  navigatePrevious() {
    this.stateMachine.executeAction('navigatePrevious')
  }

  navigateNext() {
    this.stateMachine.executeAction('navigateNext')
  }

  changeAnswer(answer: Data.ExamQuestionAnswer,
               evaluation: Data.ExamQuestionEvaluationResult) {
    this.stateMachine.executeAction('changeAnswer', answer, evaluation)
  }

  changeState(state: Data.ExamQuestionState) {
    this.stateMachine.executeAction('changeState', state)
  }

  resetQuestion() {
    this.stateMachine.executeAction('resetQuestion')
  }

  finish() {
    this.stateMachine.executeAction('finish')
  }

  reset() {
    this.stateMachine.executeAction('reset')
  }

  cancel() { 
    this.stateMachine.executeAction('cancel')
  }
  
  private handleStarted() {
    const events = {
      ...this.commonEventHandlers,
      onFinished: (details: States.InProgressFinishedDetails) => {
      this.gotoFinished()
      if (details.isTimeExpired && this.onTimeExpired) {
        this.onTimeExpired()
      }
    }
    }
    const inProgressState = new States.ExamInProgressState(this.data,
                                                    this.examResultService,
                                                    this.examSessionService)
    this.stateMachine.changeState(inProgressState, events)
  }

  private handleReview(questionIndex: number) {
    const events = {
      ...this.commonEventHandlers,
      onReviewFinished: () => {
      this.handleReviewFinished()
    } 
    }
    
    const reviewState = new States.ExamReviewState(questionIndex, this.data)
    this.stateMachine.changeState(reviewState, events)
  }

  private handleReviewFinished() {
    this.gotoFinished()
  }

  private gotoFinished() {
    const events = {
      ...this.commonEventHandlers,
      onReviewRequested: (questionIndex: number) => {
      this.handleReview(questionIndex)
      },
      onReset: () => {
        this.handleReset()
      } 
    }
    
    const summaryState = new States.ExamSummaryState(this.data)
    this.stateMachine.changeState(summaryState, events)
  }

  private handleReset() {
    debug('handleReset')
    this.gotoInitialState()
  }

  private gotoInitialState() {
    const events = {
      ...this.commonEventHandlers,
      onStartRequested: (options: States.ExamStartOptions) => this.handleStartRequested(options)
    }
    const initialState = new States.ExamInitialState(this.data)
    this.stateMachine.changeState(initialState, events)
  }

  private handleStartRequested(options: States.ExamStartOptions) {
    const startingState = this.createExamStartingState(options)
    this.stateMachine.changeState(startingState.state, startingState.events)
  }

  private createExamStartingState(options: States.ExamStartOptions) {
    const loadExamJob = new States.LoadExamJob(this.examStore)
    const loadQuestionsJob = new LoadQuestionsJob(this.questionStore)
    const events = {
      ...this.commonEventHandlers,
      onStarted: () => {
        this.handleStarted()
      }
    }
    const startingState = new States.ExamStartingState(options,
                                                loadExamJob, loadQuestionsJob, this.data,
                                                this.examSessionService)
    return { state: startingState, events: events }
  }

  private handleCanceled() {
    const canceledState = new States.ExamCanceledState()
    this.stateMachine.changeState(canceledState, this.commonEventHandlers)
  }

  private handleError(err: unknown) {
    const events = {
      ...this.commonEventHandlers,
      onReset: () => this.handleReset()
    }
    
    const errorState = new States.ExamErrorState(this.data, err)
    this.stateMachine.changeState(errorState, events)
  }

  private get commonEventHandlers(): States.ExamStateMachineCommonEvents {
    return {
      onCanceled: () => this.handleCanceled(),
      onError: (err) => this.handleError(err)
    }
  }

  addStateChangedListener(listener: StateChangedListener<States.ExamStateMachinePhase>) {
    return this.stateMachine.addStateChangedListener(listener)
  }
  
  removeStateChangedListener(token: number) {
    this.stateMachine.removeStateChangedListener(token)
  }

  get error() {
    return this.data.error
  }
}
